import * as con from "../Constants";
import {
  checkServerHealth,
  uploadFile,
  createAnnotationJob,
  checkJobStatus,
  getJobResults
} from '../utils/apiFunctions';
import { setStoredValue } from '../store/actions';

/**
 * Service for handling the annotation workflow
 * This module coordinates the steps to:
 * 1. Check server health
 * 2. Upload file
 * 3. Create annotation job
 * 4. Monitor job status
 * 5. Retrieve results when job is complete
 */
export default class AnnotationService {
  /**
   * Run an annotation job with the specified method
   * @param {File} file           - The file to annotate
   * @param {string} method       - The annotation method
   * @param {string} database     - The database to use
   * @param {Function} onProgress - Callback for progress updates
   * @param {Function} onComplete - Callback for when job is complete, with results
   * @param {Function} onError    - Callback for error handling
   */
  static async runAnnotation(file, method, database, onProgress, onComplete, onError) {

    // Resest bug report varaibles
    setStoredValue(con.CURRENT_FILE_ID, null);
    setStoredValue(con.CURRENT_JOB_ID, null);


    try {
      // Step 1: Check server health
      onProgress('Checking server health...');
      const healthCheck = await checkServerHealth();

      if (healthCheck.status !== 'healthy') {
        throw new Error('Server is not healthy');
      }

      // Step 2: Upload file
      onProgress('Uploading file...');
      const uploadResponse = await uploadFile(file);

      if (uploadResponse.upload_status !== 'success') {
        throw new Error(`File upload failed: ${uploadResponse.message}`);
      }

      // Store file ID (also used for bug reports)
      setStoredValue(con.CURRENT_FILE_ID, uploadResponse.file_id);

      // Step 3: Create annotation job
      onProgress('Creating annotation job...');

      // Determine tool and algorithm based on method
      let tool, algorithm;
      switch (method) {
        case con.ANNOTATION_METHOD_BLAST:
          tool = 'blast';
          algorithm = 'blastn';
          break;
        case con.ANNOTATION_METHOD_MEGABLAST:
          tool = 'blast';
          algorithm = 'megablast';
          break;
        case con.ANNOTATION_METHOD_VSEARCH:
          tool = 'vsearch';
          algorithm = 'usearch_global';
          break;
        case con.ANNOTATION_METHOD_EXACT:
          tool = 'vsearch';
          algorithm = 'search_exact';
          break;
        default:
          throw new Error(`Unknown annotation method: ${method}`);
      }

      // Map frontend database names to server-expected database names
      let serverDatabase;
      switch (database) {
        case con.DATABASE_ITS:
          serverDatabase = 'eukaryome_its';
          break;
        case con.DATABASE_SSU:
          serverDatabase = 'eukaryome_ssu';
          break;
        case con.DATABASE_LSU:
          serverDatabase = 'eukaryome_lsu';
          break;
        case con.DATABASE_LrRNA:
          serverDatabase = 'eukaryome_long';
          break;
        default:
          throw new Error(`Unknown database: ${database}`);
      }

      const jobResponse = await createAnnotationJob(
        uploadResponse.file_id,
        tool,
        algorithm,
        serverDatabase
      );

      // Store job ID and method (also used for bug reports)
      setStoredValue(con.CURRENT_JOB_ID, jobResponse.job_id);
      setStoredValue(con.SELECTED_METHOD, method);

      // Step 4: Monitor job status
      onProgress('Processing annotation...');
      let jobStatus = await this.monitorJobStatus(jobResponse.job_id, onProgress);

      if (jobStatus.status !== 'finished') {
        throw new Error(`Job failed with status: ${jobStatus.status}`);
      }

      // Step 5: Get results
      onProgress('Retrieving results...');
      const results = await getJobResults(jobStatus.job_id);

      // Step 6: Done!
      onComplete(results);

    } catch (error) {
      console.error('Annotation process failed:', error);
      onError(error.message);
    }
  }

  /**
   * Monitor job status with polling
   * @param {string} jobId        - The ID of the job to monitor
   * @param {Function} onProgress - Callback for progress updates
   * @returns {Promise<Object>}   - The final job status
   */
  static async monitorJobStatus(jobId, onProgress) {
    // Initial poll interval for first 10 seconds
    const INITIAL_POLL_INTERVAL = 2000;
    // Number of attempts at initial interval (10 seconds / 2 seconds = 5 attempts)
    const INITIAL_ATTEMPTS = 5;
    // Maximum interval between polls (cap at 32 seconds to follow exponential series)
    const MAX_POLL_INTERVAL = 32000;
    // Maximum total time (10 minutes)
    const MAX_TOTAL_TIME = 10 * 60 * 1000;

    let attempts = 0;
    let timeoutId = null;
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const calculatePollInterval = (attemptNumber) => {
        if (attemptNumber < INITIAL_ATTEMPTS) {
          // Use initial interval for first 10 seconds
          return INITIAL_POLL_INTERVAL;
        } else {
          // Exponential back-off: 2s, 4s, 8s, 16s, 32s (capped)
          const exponentialInterval = INITIAL_POLL_INTERVAL * Math.pow(2, attemptNumber - INITIAL_ATTEMPTS);
          return Math.min(exponentialInterval, MAX_POLL_INTERVAL);
        }
      };

      const checkStatus = async () => {
        try {
          const statusResponse = await checkJobStatus(jobId);

          switch (statusResponse.status) {
            case 'finished':
              cleanup();
              resolve(statusResponse);
              break;
            case 'failed':
              cleanup();
              reject(new Error(`Job failed: ${statusResponse.message || 'Unknown error'}`));
              break;
            case 'queued':
            case 'running':
              // Continue waiting
              if (statusResponse.progress) {
                onProgress(`Processing: ${statusResponse.progress}% complete`);
              } else {
                onProgress(`Job status: ${statusResponse.status}`);
              }

              attempts++;

              // Check if we've exceeded the maximum total time
              const elapsedTime = Date.now() - startTime;
              if (elapsedTime >= MAX_TOTAL_TIME) {
                cleanup();
                reject(new Error('Job timed out after 10 minutes'));
              } else {
                const nextInterval = calculatePollInterval(attempts);
                timeoutId = setTimeout(checkStatus, nextInterval);
              }
              break;
            default:
              cleanup();
              reject(new Error(`Unknown job status: ${statusResponse.status}`));
          }
        } catch (error) {
          cleanup();
          reject(error);
        }
      };

      // Start polling
      checkStatus();
    });
  }
}

